import { useEffect, useRef } from 'react';
import { useReduxState } from 'hooks';
import { EditorBlockTranslationReference } from 'components/editor';
import { RichTextValue } from 'components/common';
import { Icon } from 'components/common/icons';
import { Form, FormGroup, NumberStepper } from 'components/common/form';
import { TaskPointWeightSelector } from 'components/common/tasks';
import {
  defaultBlockData,
  EditorBlockIsMandatorySettings,
  EditorBlockMoreSettings,
  EditorBlockRequireAnswerToProceedInfo
} from 'components/editor/blocks';
import { TextEditor } from 'components/editor/inputs';

import type { FormApi } from 'informed';
import type { BlockNumericQuestion } from 'shared-types';
import type {
  BlockProps,
  EditorStateProps
} from 'components/editor/blocks/EditorBlock';

type StateProps = Pick<
  EditorStateProps,
  'editorLanguageActive' | 'editorLanguageReference'
>;

const EditorBlockNumericQuestion = ({
  blockId,
  data,
  isAIPreview,
  isPreview,
  languageVersion,
  updateData,
  ...stateProps
}: BlockProps<BlockNumericQuestion> & StateProps) => {
  const { editorLanguageActive, editorLanguageReference } = stateProps;

  const {
    messages,
    pageState: { course },
    theme
  } = useReduxState();
  const formApi = useRef<FormApi>(null);

  useEffect(() => {
    if (data.correctAnswer && formApi.current) {
      const currentValue = formApi.current.getState().values.correctAnswer;
      if (data.correctAnswer !== currentValue) {
        formApi.current.setValue('correctAnswer', data.correctAnswer);
      }
    }
  }, [data.correctAnswer]);

  const setFormApi = (api: FormApi) => {
    formApi.current = api;
  };

  const handleQuestionChange = (html: string) => {
    updateData({
      ...data,
      locales: {
        ...data.locales,
        [languageVersion]: {
          ...data.locales[languageVersion],
          translatable: {
            ...data.locales[languageVersion].translatable,
            question: html
          }
        }
      }
    });
  };

  const handleAnswerChange = (number: number) => {
    updateData({ ...data, correctAnswer: number });
  };

  const handleFeedbackCorrectChange = (html: string) => {
    updateData({
      ...data,
      locales: {
        ...data.locales,
        [languageVersion]: {
          ...data.locales[languageVersion],
          translatable: {
            ...data.locales[languageVersion].translatable,
            feedbackCorrect: html
          }
        }
      }
    });
  };

  const handleFeedbackIncorrectChange = (html: string) => {
    updateData({
      ...data,
      locales: {
        ...data.locales,
        [languageVersion]: {
          ...data.locales[languageVersion],
          translatable: {
            ...data.locales[languageVersion].translatable,
            feedbackIncorrect: html
          }
        }
      }
    });
  };

  const handlePointsWeightChange = (value: number) => {
    updateData({ ...data, pointsWeight: value });
  };

  const pointsWeight = data.hasOwnProperty('pointsWeight')
    ? data.pointsWeight
    : defaultBlockData.numericquestion.pointsWeight;

  const previewFeedbackStyle = {
    backgroundColor: 'white',
    border: `1px solid ${theme.lightGreyDark}`,
    borderRadius: theme.inputBorderRadius,
    color: theme.textColorLight,
    padding: 10
  };

  const isEditingLanguageVersion = Boolean(editorLanguageActive);

  return (
    <div className="numeric-question">
      <FormGroup maxWidth="100%">
        {!isAIPreview ? (
          <TaskPointWeightSelector
            blockId={blockId}
            blockName={messages.blocksNumericQuestion}
            disabled={isPreview || isEditingLanguageVersion}
            isMandatory={data.isMandatory}
            pointsWeight={pointsWeight}
            onSelection={handlePointsWeightChange}
          />
        ) : null}

        {isAIPreview || isPreview ? (
          <RichTextValue
            html={data.locales[languageVersion].translatable.question}
          />
        ) : (
          <>
            <EditorBlockTranslationReference
              backgroundColor={theme.lightGrey}
              isInnerElement={true}
              referenceHtml={
                data.locales[editorLanguageReference]?.translatable?.question ||
                ''
              }
              translationHtml={
                data.locales[languageVersion].translatable.question
              }
            >
              <TextEditor
                ariaLabel={messages.commonQuestion}
                backgroundColor="inherit"
                content={data.locales[languageVersion].translatable.question}
                onChange={handleQuestionChange}
                padding="0"
                placeholder={messages.editorBlockNumericQuestionPlaceholder}
              />
            </EditorBlockTranslationReference>
          </>
        )}
      </FormGroup>

      <Form getApi={setFormApi}>
        <FormGroup
          label={messages.commonCorrectAnswer}
          maxWidth="100%"
          style={{ marginTop: 15 }}
        >
          <NumberStepper
            content={data.correctAnswer}
            disabled={isAIPreview || isPreview || isEditingLanguageVersion}
            field="correctAnswer"
            id={`numeric-question-correct-answer-${blockId}`}
            initialValue={data.correctAnswer || 0}
            inputStyle={{ width: 200 }}
            onChange={handleAnswerChange}
            precision={0}
            stepValue={1}
          />
        </FormGroup>
      </Form>

      <FormGroup
        label={
          <div className="result-label">
            <div className="result-label-icon">
              <Icon
                icon="check-circle"
                style={{ color: theme.successStandaloneColor }}
              />
            </div>
            {messages.editorBlockQuestionFeedbackCorrect}
          </div>
        }
        maxWidth="100%"
      >
        {isAIPreview || isPreview ? (
          <RichTextValue
            html={
              data.locales[languageVersion].translatable.feedbackCorrect || ''
            }
            style={previewFeedbackStyle}
          />
        ) : (
          <>
            <EditorBlockTranslationReference
              backgroundColor={theme.lightGrey}
              isInnerElement={true}
              key={editorLanguageReference}
              referenceHtml={
                data.locales[editorLanguageReference]?.translatable
                  ?.feedbackCorrect || ''
              }
              style={{ marginTop: 5 }}
              translationHtml={
                data.locales[languageVersion].translatable.feedbackCorrect || ''
              }
            >
              <TextEditor
                content={
                  data.locales[languageVersion].translatable.feedbackCorrect ||
                  ''
                }
                onChange={handleFeedbackCorrectChange}
                placeholder={messages.commonFeedbackPlaceholder}
                showBorder={true}
              />
            </EditorBlockTranslationReference>
          </>
        )}
      </FormGroup>

      <FormGroup
        label={
          <div className="result-label">
            <div className="result-label-icon">
              <Icon icon="times-circle" style={{ color: theme.dangerColor }} />
            </div>
            {messages.editorBlockQuestionFeedbackIncorrect}
          </div>
        }
        maxWidth="100%"
      >
        {isAIPreview || isPreview ? (
          <RichTextValue
            html={
              data.locales[languageVersion].translatable.feedbackIncorrect || ''
            }
            style={previewFeedbackStyle}
          />
        ) : (
          <>
            <EditorBlockTranslationReference
              backgroundColor={theme.lightGrey}
              isInnerElement={true}
              key={editorLanguageReference}
              referenceHtml={
                data.locales[editorLanguageReference]?.translatable
                  ?.feedbackIncorrect || ''
              }
              style={{ marginTop: 5 }}
              translationHtml={
                data.locales[languageVersion].translatable.feedbackIncorrect ||
                ''
              }
            >
              <TextEditor
                content={
                  data.locales[languageVersion].translatable
                    .feedbackIncorrect || ''
                }
                onChange={handleFeedbackIncorrectChange}
                placeholder={messages.commonFeedbackPlaceholder}
                showBorder={true}
              />
            </EditorBlockTranslationReference>
          </>
        )}
      </FormGroup>

      {!isAIPreview && !isEditingLanguageVersion ? (
        <EditorBlockMoreSettings
          settingsHaveBeenEdited={!isPreview && Boolean(data?.isMandatory)}
        >
          <EditorBlockIsMandatorySettings
            blockId={blockId}
            data={data}
            disabled={isPreview}
            updateData={updateData}
          />
        </EditorBlockMoreSettings>
      ) : null}

      {!isAIPreview ? (
        <EditorBlockRequireAnswerToProceedInfo data={data} />
      ) : null}

      <style jsx>{`
        .numeric-question {
          background-color: ${theme.lightGrey};
          border-radius: ${theme.cardBorderRadius};
          padding: 15px;
        }
        .result-label {
          align-items: center;
          display: flex;
        }
        .result-label-icon {
          font-size: 1.1rem;
          margin-right: 5px;
          position: relative;
          top: 1px;
        }
      `}</style>
    </div>
  );
};

export default EditorBlockNumericQuestion;
